import {
  type AuthConfirmResetPasswordT,
  type AuthLoginCredentialsT,
  type AuthRegisterCredentialsT,
} from '@ruminati/types_v2/auth'
import { type UserWithOrganisationsAndRelationshipsT } from '@ruminati/types_v2/user'
import { HTTPError } from 'ky'
import { type Pinia, storeToRefs } from 'pinia'
import { computed, readonly, ref } from 'vue'

import {
  requestLogout,
  useConfirmResetPasswordMutation,
  useLoginWithCredentialsMutation,
  useLogoutMutation,
  useRegisterWithCredentialsMutation,
  useSendForgotPasswordEmailMutation,
  useSendVerificationEmailMutation,
} from '@/queries/auth'
import { queryKeysProfile } from '@/queries/users'
import { queryClient } from '@/services/queryClient'
import { useAgriwebbStore } from '@/stores/agriwebb'
import { useEmissionsReportsStore } from '@/stores/emissionsReports'
import { useLivestockStore } from '@/stores/livestock'
import { useNotificationsStore } from '@/stores/notifications'
import { useOrganisationStore } from '@/stores/organisations'
import { usePropertiesStore } from '@/stores/properties'
import { useUiStore } from '@/stores/ui'
import { useUsersStore } from '@/stores/users'
import {
  sendBroadcastMessageFromTabIfFocused,
  setHasActiveSession,
  SESSION_CHANGE_REASONS,
} from '@/utilities/broadcaster'
import { clearPersistedReportCommentDrafts } from '@/utilities/commentDrafts'
import { rollbar } from '@/utilities/rollbar'

type AuthResult<T> = { success: true; data: T } | { success: false; error: unknown }

const lastAttemptedSendingVerificationEmail = ref<number>()
const lastAttemptedSendingForgotPasswordEmail = ref<number>()

async function parseAuthError(error: unknown, includeContext = false): Promise<unknown> {
  if (error instanceof HTTPError) {
    try {
      const body: unknown = await error.response.clone().json()
      if (
        typeof body === 'object' &&
        body !== null &&
        'code' in body &&
        typeof body.code === 'string'
      ) {
        if (
          includeContext &&
          'context' in body &&
          typeof body.context === 'object' &&
          body.context !== null
        ) {
          return { code: body.code, context: body.context }
        }
        return body.code
      }
    } catch {
      // Return the original error when the response does not contain JSON.
    }
  }
  return error
}

function applyAuthenticatedSession(response: UserWithOrganisationsAndRelationshipsT) {
  const userStore = useUsersStore()
  const organisationStore = useOrganisationStore()

  if (response.organisations !== undefined) {
    organisationStore.upsertOrganisations(response.organisations)
  }
  if (response.relationships !== undefined) {
    userStore.setRelationships(response.relationships)
  }
  if (response.pendingOrgInvitations !== undefined) {
    organisationStore.setPendingOrgInvitations(response.pendingOrgInvitations)
  }
  userStore.setProfile(response.user)
  organisationStore.initializeCurrentOrg(response.user)
  queryClient.setQueryData(queryKeysProfile.all(), response.user)
}

export function initializeAuth(pinia: Pinia) {
  setHasActiveSession(() => {
    const usersState: unknown = pinia.state.value.users
    return (
      typeof usersState === 'object' &&
      usersState !== null &&
      'profile' in usersState &&
      usersState.profile !== undefined
    )
  })
}

export function clearLocalSession() {
  clearPersistedReportCommentDrafts()

  const propertiesStore = usePropertiesStore()
  const emissionsReportsStore = useEmissionsReportsStore()
  const notificationsStore = useNotificationsStore()
  const organisationStore = useOrganisationStore()
  const agriwebbStore = useAgriwebbStore()
  const livestockStore = useLivestockStore()
  const uiStore = useUiStore()
  const usersStore = useUsersStore()

  queryClient.clear()
  usersStore.reset()
  propertiesStore.resetAll()
  emissionsReportsStore.resetAll()
  notificationsStore.reset()
  organisationStore.reset()
  agriwebbStore.reset()
  livestockStore.resetAll()
  uiStore.reset()

  rollbar.configure({
    payload: {
      person: undefined,
    },
  })
}

function completeLogout() {
  clearLocalSession()
  sendBroadcastMessageFromTabIfFocused(SESSION_CHANGE_REASONS.LOGGED_OUT)
}

export async function logoutCurrentSession(): Promise<AuthResult<void>> {
  try {
    await requestLogout()
    completeLogout()
    return { success: true, data: undefined }
  } catch (error) {
    return { success: false, error }
  }
}

export function useAuth() {
  const usersStore = useUsersStore()
  const { profile: currentUser } = storeToRefs(usersStore)
  const isAuthenticated = computed(() => currentUser.value !== undefined)

  return {
    currentUser,
    isAuthenticated,
  }
}

export function useRegisterWithCredentials() {
  const mutation = useRegisterWithCredentialsMutation()
  const registerWithCredentialsError = ref<unknown>()

  async function registerWithCredentials(
    details: AuthRegisterCredentialsT,
  ): Promise<AuthResult<UserWithOrganisationsAndRelationshipsT>> {
    registerWithCredentialsError.value = undefined
    try {
      const response = await mutation.mutateAsync(details)
      applyAuthenticatedSession(response)
      sendBroadcastMessageFromTabIfFocused(SESSION_CHANGE_REASONS.USER_CHANGED)
      return { success: true, data: response }
    } catch (error) {
      registerWithCredentialsError.value = await parseAuthError(error)
      return { success: false, error }
    }
  }

  return {
    isRegisteringWithCredentials: mutation.isPending,
    registerWithCredentials,
    registerWithCredentialsError: readonly(registerWithCredentialsError),
  }
}

export function useLoginWithCredentials() {
  const mutation = useLoginWithCredentialsMutation()
  const loginWithCredentialsError = ref<unknown>()

  async function loginWithCredentials(
    details: AuthLoginCredentialsT,
  ): Promise<AuthResult<UserWithOrganisationsAndRelationshipsT>> {
    loginWithCredentialsError.value = undefined
    try {
      const response = await mutation.mutateAsync(details)
      applyAuthenticatedSession(response)
      sendBroadcastMessageFromTabIfFocused(SESSION_CHANGE_REASONS.USER_CHANGED)
      return { success: true, data: response }
    } catch (error) {
      loginWithCredentialsError.value = await parseAuthError(error)
      return { success: false, error }
    }
  }

  return {
    isLoggingInWithCredentials: mutation.isPending,
    loginWithCredentials,
    loginWithCredentialsError: readonly(loginWithCredentialsError),
  }
}

export function useSendVerificationEmail() {
  const mutation = useSendVerificationEmailMutation()
  const sendVerificationEmailError = ref<unknown>()

  async function sendVerificationEmail(details: { email: string }): Promise<AuthResult<void>> {
    lastAttemptedSendingVerificationEmail.value = Date.now()
    sendVerificationEmailError.value = undefined
    try {
      await mutation.mutateAsync(details)
      return { success: true, data: undefined }
    } catch (error) {
      const parsedError = await parseAuthError(error, true)
      sendVerificationEmailError.value = parsedError
      return { success: false, error: parsedError }
    }
  }

  return {
    isSendingVerificationEmail: mutation.isPending,
    lastAttemptedSendingVerificationEmail: readonly(lastAttemptedSendingVerificationEmail),
    sendVerificationEmail,
    sendVerificationEmailError: readonly(sendVerificationEmailError),
  }
}

export function useSendForgotPasswordEmail() {
  const mutation = useSendForgotPasswordEmailMutation()
  const sendForgotPasswordEmailError = ref<unknown>()

  async function sendForgotPasswordEmail(details: { email: string }): Promise<AuthResult<void>> {
    lastAttemptedSendingForgotPasswordEmail.value = Date.now()
    sendForgotPasswordEmailError.value = undefined
    try {
      await mutation.mutateAsync(details)
      return { success: true, data: undefined }
    } catch (error) {
      sendForgotPasswordEmailError.value = await parseAuthError(error)
      return { success: false, error }
    }
  }

  return {
    isSendingForgotPasswordEmail: mutation.isPending,
    lastAttemptedSendingForgotPasswordEmail: readonly(lastAttemptedSendingForgotPasswordEmail),
    sendForgotPasswordEmail,
    sendForgotPasswordEmailError: readonly(sendForgotPasswordEmailError),
  }
}

export function useConfirmResetPassword() {
  const mutation = useConfirmResetPasswordMutation()
  const confirmResetPasswordError = ref<unknown>()

  async function confirmResetPassword(
    details: AuthConfirmResetPasswordT,
  ): Promise<AuthResult<void>> {
    confirmResetPasswordError.value = undefined
    try {
      await mutation.mutateAsync(details)
      return { success: true, data: undefined }
    } catch (error) {
      confirmResetPasswordError.value = await parseAuthError(error)
      return { success: false, error }
    }
  }

  function resetConfirmResetPassword() {
    confirmResetPasswordError.value = undefined
    mutation.reset()
  }

  return {
    confirmResetPassword,
    confirmResetPasswordError: readonly(confirmResetPasswordError),
    isConfirmingResetPassword: mutation.isPending,
    resetConfirmResetPassword,
  }
}

export function useLogout() {
  const mutation = useLogoutMutation()
  const logoutError = ref<unknown>()

  async function logout(): Promise<AuthResult<void>> {
    logoutError.value = undefined
    try {
      await mutation.mutateAsync()
      completeLogout()
      return { success: true, data: undefined }
    } catch (error) {
      logoutError.value = await parseAuthError(error)
      return { success: false, error }
    }
  }

  return {
    isLoggingOut: mutation.isPending,
    logout,
    logoutError: readonly(logoutError),
  }
}
